/** @jsxImportSource @emotion/react */

import { FC, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-final-form'
import { Checkbox } from 'components/Checkbox'
import { InputField } from 'components/Input'
import { validateRequired, validateRequiredHtml } from 'utils/validation'
import { EditorField } from 'components/Editor'
import { QuestionTypeEnum } from '__generatedTypes__/globalTypes'
import TabsNavigation from 'components/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import { AdditionalFieldsConfig, FormState } from '../consts'
import * as formClasses from '../styles'
import * as commonClasses from '../commonStyles'
import { EndodonticTable, OrthodonticTable, PeriodonticsTable, SimpleEndodonticTable } from './TableFields'
import { AdditionalField } from './AdditionalField'
import * as classes from './styles'

type Props = {
  values: FormState
  config: AdditionalFieldsConfig
}

export const AdditionalFields: FC<Props> = ({ config, values }) => {
  const form = useForm<FormState, Partial<FormState>>()
  const [activeTableTab, setActiveTableTab] = useState<TabsItem | null>(null)

  const selectedTableFields = useMemo(
    () => config.tableFields.filter(({ isNeedKey }) => Boolean(values[isNeedKey])),
    [config.tableFields, values]
  )

  const tableTabs: TabsItem[] = useMemo(
    () =>
      selectedTableFields.map(({ formKey, label }) => ({
        id: formKey,
        title: label,
      })),
    [selectedTableFields]
  )

  // Set active tab when tabs change
  useEffect(() => {
    if (tableTabs.length > 0 && (!activeTableTab || !tableTabs.find((tab) => tab.id === activeTableTab.id))) {
      setActiveTableTab(tableTabs[0])
    } else if (tableTabs.length === 0) {
      setActiveTableTab(null)
    }
  }, [tableTabs, activeTableTab])

  const renderTableFields = () => {
    if (config.tableFields.length === 0) {
      return null
    }

    return (
      <div css={classes.tableFieldsSection}>
        <div css={classes.tableFieldsCheckboxes}>
          {config.tableFields.map(({ formKey, isNeedKey, label }) => (
            <Checkbox
              key={formKey}
              label={`Do you need ${label}?`}
              checked={Boolean(values[isNeedKey])}
              onChange={(_, value) => form.change(isNeedKey, value)}
              css={classes.tableFieldCheckbox}
            />
          ))}
        </div>
        {selectedTableFields.length > 0 && tableTabs.length > 0 && (
          <div css={classes.tableFieldsTabs}>
            {tableTabs.length > 1 && (
              <TabsNavigation
                tabs={tableTabs}
                value={activeTableTab || tableTabs[0]}
                onChange={(tab: TabsItem) => setActiveTableTab(tab)}
              />
            )}
            {activeTableTab && (
              <>
                <div css={formClasses.title}>{activeTableTab.title}</div>
                <div css={classes.tableFieldContent}>
                  {activeTableTab.id === 'endodonticTable' && (
                    <EndodonticTable
                      name="endodonticTable"
                      value={values.endodonticTable as string | null | undefined}
                    />
                  )}
                  {activeTableTab.id === 'orthodonticTable' && (
                    <OrthodonticTable
                      name="orthodonticTable"
                      value={values.orthodonticTable as string | null | undefined}
                    />
                  )}
                  {activeTableTab.id === 'periodontalTable' && (
                    <PeriodonticsTable
                      name="periodontalTable"
                      value={values.periodontalTable as string | null | undefined}
                    />
                  )}
                  {activeTableTab.id === 'simpleEndodonticTable' && (
                    <SimpleEndodonticTable
                      name="simpleEndodonticTable"
                      value={values.simpleEndodonticTable as string | null | undefined}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderPatientBox = () => (
    <>
      <div css={formClasses.title}>Patient Box</div>
      <div css={classes.patientBox}>
        {config.patientBoxFields.map(({ key, label, required }) => {
          const fieldProps = {
            key: key,
            name: key,
            label,
            placeholder: `Enter ${label}`,
            css: commonClasses.editor,
          }

          return !config.patientBoxAsHtml ? (
            <InputField {...fieldProps} multiline fullWidth validate={required ? validateRequired : undefined} />
          ) : (
            <EditorField type="default" {...fieldProps} validate={required ? validateRequiredHtml : undefined} />
          )
        })}
      </div>
    </>
  )

  return (
    <div css={classes.additionalFields}>
      {renderTableFields()}

      {config.otherFields.map((fieldConfig) => (
        <AdditionalField key={fieldConfig.key} config={fieldConfig} values={values} form={form} />
      ))}

      <div css={classes.patientBoxWrapper(config.patientBoxAtTop)}>
        {values.type === QuestionTypeEnum.RxWritingNDEB ? (
          renderPatientBox()
        ) : (
          <>
            <Checkbox
              label={`Do you need ${config.patientBoxLabel}?`}
              checked={Boolean(values.isNeedPatientBox)}
              onChange={(_, value) => form.change('isNeedPatientBox', value)}
              css={classes.field}
            />
            {!!values.isNeedPatientBox && renderPatientBox()}
          </>
        )}
      </div>
    </div>
  )
}
